import React, { useState } from 'react';
import { Card } from '../../components/ui/Card';
import { 
  TrendingUp, 
  TrendingDown, 
  AlertCircle,
  ArrowUp,
  ArrowDown,
  DollarSign,
  Target,
  Users,
  MousePointerClick,
  Percent
} from 'lucide-react';
import { Line } from 'react-chartjs-2';

// Sample data for the last 5 months
const historicalData = [
  {
    month: 'January',
    spend: 125000,
    revenue: 375000,
    roas: 3.0,
    conversions: 1250,
    cpa: 100,
    clicks: 25000,
    ctr: 2.5,
    impressions: 1000000
  },
  {
    month: 'February',
    spend: 135000,
    revenue: 432000,
    roas: 3.2,
    conversions: 1440,
    cpa: 93.75,
    clicks: 27000,
    ctr: 2.7,
    impressions: 1000000
  },
  {
    month: 'March',
    spend: 142000,
    revenue: 497000,
    roas: 3.5,
    conversions: 1580,
    cpa: 89.87,
    clicks: 28400,
    ctr: 2.84,
    impressions: 1000000
  },
  {
    month: 'April',
    spend: 150000,
    revenue: 525000,
    roas: 3.5,
    conversions: 1650,
    cpa: 90.91,
    clicks: 30000,
    ctr: 3.0,
    impressions: 1000000
  },
  {
    month: 'May',
    spend: 155000,
    revenue: 558000,
    roas: 3.6,
    conversions: 1720,
    cpa: 90.12,
    clicks: 31000,
    ctr: 3.1,
    impressions: 1000000
  }
];

// Prediction for June
const prediction = {
  month: 'June',
  spend: {
    value: 162000,
    change: 4.5,
    confidence: 92
  },
  revenue: {
    value: 599400,
    change: 7.4,
    confidence: 88
  },
  roas: {
    value: 3.7,
    change: 2.8,
    confidence: 85
  },
  conversions: {
    value: 1800,
    change: 4.7,
    confidence: 90
  },
  cpa: {
    value: 90.00,
    change: -0.13,
    confidence: 87
  }
};

// Chart data
const chartData = {
  labels: [...historicalData.map(d => d.month), prediction.month],
  datasets: [
    {
      label: 'Spend',
      data: [...historicalData.map(d => d.spend), prediction.spend.value],
      borderColor: 'rgb(59, 130, 246)',
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      fill: true,
      tension: 0.4,
      borderWidth: 2,
      pointRadius: 4,
      pointBackgroundColor: 'rgb(59, 130, 246)'
    },
    {
      label: 'Revenue',
      data: [...historicalData.map(d => d.revenue), prediction.revenue.value],
      borderColor: 'rgb(34, 197, 94)',
      backgroundColor: 'rgba(34, 197, 94, 0.1)',
      fill: true,
      tension: 0.4,
      borderWidth: 2,
      pointRadius: 4,
      pointBackgroundColor: 'rgb(34, 197, 94)'
    }
  ]
};

const chartOptions = {
  responsive: true,
  plugins: {
    legend: {
      position: 'top',
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      backgroundColor: 'white',
      titleColor: '#1f2937',
      bodyColor: '#1f2937',
      borderColor: '#e5e7eb',
      borderWidth: 1,
      padding: 12,
      bodyFont: {
        size: 12,
        family: "'Inter', sans-serif",
      },
      titleFont: {
        size: 12,
        family: "'Inter', sans-serif",
        weight: '600',
      },
      callbacks: {
        label: function(context) {
          let label = context.dataset.label || '';
          if (label) {
            label += ': ';
          }
          if (context.parsed.y !== null) {
            label += new Intl.NumberFormat('tr-TR', { 
              style: 'currency', 
              currency: 'TRY',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            }).format(context.parsed.y);
          }
          return label;
        }
      }
    }
  },
  scales: {
    x: {
      grid: {
        display: false,
      },
      ticks: {
        font: {
          size: 12,
          family: "'Inter', sans-serif",
        },
        color: '#6b7280',
      }
    },
    y: {
      grid: {
        color: '#f3f4f6',
      },
      ticks: {
        font: {
          size: 12,
          family: "'Inter', sans-serif",
        },
        color: '#6b7280',
        callback: function(value) {
          return new Intl.NumberFormat('tr-TR', { 
            style: 'currency', 
            currency: 'TRY',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          }).format(value);
        }
      }
    }
  }
};

export default function Forecasting() {
  return (
    <div className="p-6 space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center bg-white p-6 rounded-2xl shadow-sm">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Budget Forecasting</h1>
          <p className="text-sm text-gray-500 mt-1">View historical performance and predictions for the next month</p>
        </div>
      </div>

      {/* Prediction Cards */}
      <div className="grid grid-cols-5 gap-6">
        <PredictionCard 
          title="Predicted Spend"
          value={prediction.spend.value}
          change={prediction.spend.change}
          confidence={prediction.spend.confidence}
          icon={DollarSign}
          format="currency"
        />
        <PredictionCard 
          title="Predicted Revenue"
          value={prediction.revenue.value}
          change={prediction.revenue.change}
          confidence={prediction.revenue.confidence}
          icon={TrendingUp}
          format="currency"
        />
        <PredictionCard 
          title="Predicted ROAS"
          value={prediction.roas.value}
          change={prediction.roas.change}
          confidence={prediction.roas.confidence}
          icon={Target}
          format="roas"
        />
        <PredictionCard 
          title="Predicted Conversions"
          value={prediction.conversions.value}
          change={prediction.conversions.change}
          confidence={prediction.conversions.confidence}
          icon={Users}
          format="number"
        />
        <PredictionCard 
          title="Predicted CPA"
          value={prediction.cpa.value}
          change={prediction.cpa.change}
          confidence={prediction.cpa.confidence}
          icon={MousePointerClick}
          format="currency"
        />
      </div>

      {/* Trend Chart */}
      <Card className="p-6 rounded-2xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Performance Trend</h2>
            <p className="text-sm text-gray-500">Historical data and predictions</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-lg">
              <div className="w-2 h-2 rounded-full bg-blue-500"></div>
              <span className="text-sm text-gray-600">Spend</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 rounded-lg">
              <div className="w-2 h-2 rounded-full bg-green-500"></div>
              <span className="text-sm text-gray-600">Revenue</span>
            </div>
          </div>
        </div>
        <div className="h-[400px]">
          <Line data={chartData} options={chartOptions} />
        </div>
      </Card>

      {/* Historical Data Table */}
      <Card className="rounded-2xl overflow-hidden">
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold text-gray-800">Historical Performance</h2>
          <p className="text-sm text-gray-500 mt-1">Last 5 months performance data</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left py-3 px-4">Month</th>
                <th className="text-right py-3 px-4">Spend</th>
                <th className="text-right py-3 px-4">Revenue</th>
                <th className="text-right py-3 px-4">ROAS</th>
                <th className="text-right py-3 px-4">Conversions</th>
                <th className="text-right py-3 px-4">CPA</th>
                <th className="text-right py-3 px-4">Clicks</th>
                <th className="text-right py-3 px-4">CTR</th>
                <th className="text-right py-3 px-4">Impressions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {historicalData.map((month) => (
                <tr key={month.month} className="hover:bg-gray-50">
                  <td className="py-3 px-4 font-medium">{month.month}</td>
                  <td className="text-right py-3 px-4">₺{month.spend.toLocaleString()}</td>
                  <td className="text-right py-3 px-4">₺{month.revenue.toLocaleString()}</td>
                  <td className="text-right py-3 px-4">{month.roas.toFixed(1)}x</td>
                  <td className="text-right py-3 px-4">{month.conversions.toLocaleString()}</td>
                  <td className="text-right py-3 px-4">₺{month.cpa.toFixed(2)}</td>
                  <td className="text-right py-3 px-4">{month.clicks.toLocaleString()}</td>
                  <td className="text-right py-3 px-4">{month.ctr}%</td>
                  <td className="text-right py-3 px-4">{month.impressions.toLocaleString()}</td>
                </tr>
              ))}
              {/* Prediction Row */}
              <tr className="bg-blue-50/50">
                <td className="py-3 px-4 font-medium text-blue-700">
                  {prediction.month} (Predicted)
                </td>
                <td className="text-right py-3 px-4 font-medium text-blue-700">
                  ₺{prediction.spend.value.toLocaleString()}
                </td>
                <td className="text-right py-3 px-4 font-medium text-blue-700">
                  ₺{prediction.revenue.value.toLocaleString()}
                </td>
                <td className="text-right py-3 px-4 font-medium text-blue-700">
                  {prediction.roas.value.toFixed(1)}x
                </td>
                <td className="text-right py-3 px-4 font-medium text-blue-700">
                  {prediction.conversions.value.toLocaleString()}
                </td>
                <td className="text-right py-3 px-4 font-medium text-blue-700">
                  ₺{prediction.cpa.value.toFixed(2)}
                </td>
                <td className="text-right py-3 px-4 font-medium text-blue-700">-</td>
                <td className="text-right py-3 px-4 font-medium text-blue-700">-</td>
                <td className="text-right py-3 px-4 font-medium text-blue-700">-</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      {/* Prediction Details */}
      <Card className="p-6 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-xl bg-blue-100">
            <AlertCircle className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-medium text-gray-800">Prediction Details</h3>
            <p className="text-sm text-gray-600 mt-2">
              These predictions are based on historical performance data and market trends. 
              The confidence score indicates the reliability of each prediction, with higher scores 
              suggesting more reliable forecasts. Factors considered include seasonal trends, 
              historical performance, and market conditions.
            </p>
            <div className="mt-4 flex items-center gap-6">
              <div>
                <span className="text-sm text-gray-500">Average Confidence Score</span>
                <p className="text-lg font-semibold text-blue-600 mt-1">
                  {Math.round(
                    (prediction.spend.confidence + 
                     prediction.revenue.confidence + 
                     prediction.roas.confidence + 
                     prediction.conversions.confidence + 
                     prediction.cpa.confidence) / 5
                  )}%
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Prediction Period</span>
                <p className="text-lg font-semibold text-gray-800 mt-1">June 2024</p>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

function PredictionCard({ title, value, change, confidence, icon: Icon, format }) {
  const formatValue = (val) => {
    switch (format) {
      case 'currency':
        return `₺${val.toLocaleString()}`;
      case 'roas':
        return `${val.toFixed(1)}x`;
      case 'number':
        return val.toLocaleString();
      default:
        return val;
    }
  };

  return (
    <Card className="p-6 rounded-2xl">
      <div className="flex items-center justify-between mb-4">
        <div className="p-3 rounded-xl bg-blue-50">
          <Icon className="w-5 h-5 text-blue-600" />
        </div>
        <div className="flex items-center gap-1">
          <Percent className="w-3 h-3 text-gray-400" />
          <span className="text-sm font-medium text-gray-400">{confidence}</span>
        </div>
      </div>
      <h3 className="text-sm font-medium text-gray-500">{title}</h3>
      <p className="text-2xl font-bold text-gray-800 mt-1">{formatValue(value)}</p>
      <div className="flex items-center gap-1 mt-2">
        {change >= 0 ? (
          <ArrowUp className="w-4 h-4 text-green-500" />
        ) : (
          <ArrowDown className="w-4 h-4 text-red-500" />
        )}
        <span className={`text-sm font-medium ${
          change >= 0 ? 'text-green-600' : 'text-red-600'
        }`}>
          {Math.abs(change)}%
        </span>
        <span className="text-sm text-gray-500">vs last month</span>
      </div>
    </Card>
  );
} 